/**
 * Mantenimiento Pagos (Cancelar Pagos) — migración web de ares4030.
 *
 * Busca un préstamo, lista todos sus recibos de pago y permite:
 *   - Cancelar el último recibo (la reversa contable la hace el endpoint
 *     /api/cancelarpagos ya existente, que valida que sea el último pago).
 *   - Reimprimir el recibo de cualquier pago (ticket).
 *   - Imprimir el reporte crediticio del préstamo.
 *
 * Igual que el escritorio, solo el recibo más reciente es cancelable; los
 * anteriores se deshacen del más nuevo al más viejo.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { imprimirReciboPdf } from "lib/recibopdf";
import { usePortalQr, portalQrActual } from "lib/portalqr";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Filtro local del préstamo: matchea por número, nombre o cédula y limita los
// resultados renderizados. Soporta opciones string (freeSolo).
const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""}`,
  limit: 60,
});

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin recibos — busque un préstamo
      </Typography>
    </Stack>
  );
}

// Imprime HTML con un iframe oculto (un window.open tras el await del fetch
// queda fuera del gesto del usuario y el navegador lo bloquea como popup).
export function imprimirHtml(html) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

const esc = (s) =>
  String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));

// Ticket de reimpresión de recibo (mismo contenido que
// procedimiento.imprimirrecibopagoprestamoanterior del escritorio), en PDF
// con el formato de bankivo.
export function imprimirTicketRecibo(recibo, compania) {
  imprimirReciboPdf({
    titulo: "Recibo de Pago (Reimpresion)",
    compania,
    campos: [
      ["Recibo #", String(recibo.norecibo || "").padStart(15, "0")],
      ["Prestamo", recibo.noprest],
      ["Cliente", recibo.nombres],
      ["Fecha", recibo.fecha],
    ],
    tabla: {
      head: ["Prest.", "Cuota", "Valor", "Mora", "Pend."],
      body: (recibo.detalle || []).map((c) => [
        recibo.noprest,
        c.nocuota,
        fmtMoney(c.valor),
        fmtMoney(c.mora),
        fmtMoney(c.pendiente),
      ]),
    },
    totales: [
      { etiqueta: "Pagado", valor: recibo.pagado },
      recibo.incauto ? { etiqueta: "Incauto/Visitas", valor: recibo.incauto } : null,
      recibo.llamadas ? { etiqueta: "Cargos", valor: recibo.llamadas } : null,
      recibo.completivo ? { etiqueta: "Completivo Inicial", valor: recibo.completivo } : null,
      { etiqueta: "Totales", valor: recibo.total, destacado: true },
      recibo.descuento ? { etiqueta: "Desc.", valor: recibo.descuento } : null,
      recibo.descuentomora ? { etiqueta: "Mora descontada", valor: recibo.descuentomora } : null,
      // Atraso al momento del pago (pagosres.montoatraso); los recibos
      // anteriores a la columna vienen con null y no llevan la línea.
      recibo.montoatraso != null ? { etiqueta: "Monto Atraso", valor: recibo.montoatraso } : null,
      { etiqueta: "Balance Pendiente", valor: recibo.balancependiente },
    ],
    notas: [
      "*Los balances pendientes no incluyen la mora,",
      "se calculara al momento de pedir su balance",
      ...(recibo.descuento ? ["**Los descuentos son valores informativos"] : []),
    ],
    firma: "Preparado Por",
    qr: portalQrActual(),
  });
}

// Reporte crediticio del préstamo (procedimiento.reimprimirfacturaunica).
function reporteCrediticio(data, compania) {
  const p = data.prestamo || {};
  const fila = (k, v) =>
    `<tr><td style="padding:2px 8px;color:#555">${k}</td><td style="padding:2px 8px;font-weight:bold">${esc(
      v
    )}</td></tr>`;
  const amort = (data.amortizacion || [])
    .map(
      (a) => `<tr>
        <td style="padding:2px 6px;border-bottom:1px solid #ddd">${esc(a.nocuota)}</td>
        <td style="padding:2px 6px;border-bottom:1px solid #ddd">${esc(a.fecha)}</td>
        <td style="padding:2px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.capital
        )}</td>
        <td style="padding:2px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.interes
        )}</td>
        <td style="padding:2px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.vpagcap
        )}</td>
        <td style="padding:2px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.vpagint
        )}</td>
        <td style="padding:2px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.vpagmora
        )}</td>
        <td style="padding:2px 6px;border-bottom:1px solid #ddd;text-align:center">${esc(
          a.status
        )}</td>
      </tr>`
    )
    .join("");
  const incautos = (data.incautos || [])
    .map(
      (i) => `<div>Chasis ${esc(i.chasis)} · RD$ ${fmtMoney(i.valor)} · ${esc(i.incautador)}</div>`
    )
    .join("");
  return `
    <html><head><meta name="color-scheme" content="light"><title>Crediticio Prestamo ${esc(p.noprest)}</title>
    <style>.cierre{margin-top:auto;page-break-inside:avoid}</style></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:20px;font-size:12px;margin:0;box-sizing:border-box;display:flex;flex-direction:column;min-height:96vh">
      <h2 style="text-align:center;margin:0 0 2px">${esc(compania?.nombre || "")}</h2>
      <p style="text-align:center;margin:0 0 12px;font-weight:bold">Reporte Crediticio — Préstamo No. ${esc(
        p.noprest
      )}</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Fecha", p.fecha)}
        ${fila("Cliente", p.nombres)}
        ${fila("Cédula", p.cedula)}
        ${fila("Dirección", [p.direccion, p.urb].filter(Boolean).join(", "))}
        ${fila("Teléfono", p.telefono)}
        ${fila("Vehículo", [p.marca, p.modelo, p.colore].filter(Boolean).join(" · "))}
        ${fila("Chasis", p.chasis)}
        ${fila("Plazo", p.plazo)}
        ${fila("Interés", p.porinteres)}
        ${fila("Monto del préstamo", "RD$ " + fmtMoney(p.cantsol))}
        ${fila("Inicial", "RD$ " + fmtMoney(p.inicial))}
        ${fila("Pagado capital", "RD$ " + fmtMoney(p.vpagcap))}
        ${fila("Pagado interés", "RD$ " + fmtMoney(p.vpagint))}
        ${fila("Pagado mora", "RD$ " + fmtMoney(p.vpagmora))}
        ${fila("Balance", "RD$ " + fmtMoney(p.balance))}
        ${fila("Último pago", `${p.fultpago || ""} (RD$ ${fmtMoney(p.montoult)})`)}
        ${fila("Status", p.status)}
        ${fila("Sucursal", p.sucursal)}
        ${p.fiador ? fila("Garante", `${p.fiador} · ${p.fiadorced} · ${p.fiadortel}`) : ""}
      </table>
      <p style="margin:14px 0 4px;font-weight:bold;border-bottom:1px solid #000">Tabla de Amortización</p>
      <table style="width:100%;border-collapse:collapse">
        <thead><tr style="background:#eee">
          <th style="padding:3px 6px;text-align:left">Cuota</th>
          <th style="padding:3px 6px;text-align:left">Fecha</th>
          <th style="padding:3px 6px;text-align:right">Capital</th>
          <th style="padding:3px 6px;text-align:right">Interés</th>
          <th style="padding:3px 6px;text-align:right">Pag. Cap.</th>
          <th style="padding:3px 6px;text-align:right">Pag. Int.</th>
          <th style="padding:3px 6px;text-align:right">Pag. Mora</th>
          <th style="padding:3px 6px;text-align:center">St.</th>
        </tr></thead>
        <tbody>${amort}</tbody>
      </table>
      ${
        incautos
          ? `<p style="margin:14px 0 4px;font-weight:bold;border-bottom:1px solid #000">Incautos Activos</p>${incautos}`
          : ""
      }
      <div class="cierre">
        <p style="margin:18px 0 0;font-size:11px">
          Declaro que he recibido y revisado el presente estado de mi financiamiento, estoy conforme con
          los balances aquí indicados y me comprometo a pagar las cuotas pendientes en las fechas
          establecidas.
        </p>
        <table style="width:100%;margin-top:55px;font-size:11px;text-align:center">
          <tr>
            <td style="width:45%;border-top:1px solid #000;padding-top:5px">
              ${esc(p.nombres) || "Cliente"}<br/>${esc(p.cedula) || ""}
            </td>
            <td style="width:10%"></td>
            <td style="width:45%;border-top:1px solid #000;padding-top:5px">Por ${esc(
              compania?.nombre || "la empresa"
            )}</td>
          </tr>
        </table>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
}

export default function MantenimientoPagos({ embedded = false, onExit, noprestInicial } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  usePortalQr();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Reimprimir un recibo tiene permiso propio (el backend re-valida). Estar en
  // el mantenimiento de pagos ya no alcanza: anular y reimprimir se conceden
  // por separado.
  const puedeReimprimir = tienePermisosModulo(roleDetalles, "reimprimirpagos");

  // Compañía para los encabezados de impresión (mismo perfil de cobro pagos).
  const [compania, setCompania] = useState(null);
  useEffect(() => {
    let activo = true;
    post("/api/cobropago_perfil", {}).then(({ ok, data }) => {
      if (activo && ok) setCompania(data.compania || null);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Buscador de préstamo ----
  // Todos los préstamos se cargan una vez al abrir (como el completer del
  // escritorio) y el Autocomplete filtra localmente.
  const [opts, setOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [prestamoSel, setPrestamoSel] = useState(null);

  useEffect(() => {
    let activo = true;
    setBuscando(true);
    post("/api/ares4030_prestamos", {})
      .then(({ ok, data }) => {
        if (activo && ok && Array.isArray(data.prestamos)) setOpts(data.prestamos);
      })
      .finally(() => {
        if (activo) setBuscando(false);
      });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Recibos ----
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const noprestActual = () =>
    typeof prestamoSel === "string" ? prestamoSel.split("-")[0].trim() : prestamoSel?.noprest;

  const cargarRecibos = useCallback(
    async (noprest) => {
      if (!noprest) {
        showAlert({ message: "Seleccione un préstamo", color: "warning" });
        return;
      }
      setLoading(true);
      try {
        const { ok, data } = await post("/api/ares4030_recibos", { noprest: String(noprest) });
        if (ok) {
          setRows((data.recibos || []).map((r, i) => ({ id: i, ...r })));
          if (!(data.recibos || []).length)
            showAlert({ message: "El préstamo no tiene pagos registrados", color: "warning" });
        } else {
          showAlert({ message: data.Error || "No se pudieron cargar los recibos", color: "error" });
        }
      } finally {
        setLoading(false);
      }
    },
    [post, showAlert]
  );

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!noprestInicial || preselListo || !opts.length) return;
    const opcion = opts.find((x) => String(x.noprest) === String(noprestInicial));
    if (opcion) { setPrestamoSel(opcion); cargarRecibos(opcion.noprest); };
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);

  // ---- Cancelación ----
  const [target, setTarget] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  // Candado síncrono: el estado tarda un render y un doble clic mandaba
  // dos anulaciones del mismo recibo (préstamo 37 de García Martínez).
  const cancelandoRef = useRef(false);

  const confirmarCancelacion = async () => {
    if (!target || cancelandoRef.current) return;
    cancelandoRef.current = true;
    setCancelando(true);
    try {
      const { ok, data } = await post("/api/cancelarpagos", { norecibo: target.norecibo });
      if (ok) {
        showAlert({ message: "Pago cancelado correctamente", color: "success" });
        setTarget(null);
        cargarRecibos(noprestActual());
      } else {
        showAlert({ message: data.Error || "No se pudo cancelar el pago", color: "error" });
      }
    } finally {
      cancelandoRef.current = false;
      setCancelando(false);
    }
  };

  // ---- Reimpresiones ----
  const reimprimir = async (norecibo) => {
    const { ok, data } = await post("/api/ares4030_reimprimirrecibo", { norecibo });
    // Multi-empresa: el backend devuelve el membrete de la empresa del pago.
    if (ok && data.recibo) imprimirTicketRecibo(data.recibo, data.compania || compania);
    else showAlert({ message: data.Error || "No se pudo recuperar el recibo", color: "error" });
  };

  const crediticio = async () => {
    const noprest = noprestActual();
    if (!noprest) return;
    const { ok, data } = await post("/api/ares4030_crediticio", { noprest: String(noprest) });
    if (ok && data.prestamo) imprimirHtml(reporteCrediticio(data, compania));
    else showAlert({ message: data.Error || "No se pudo recuperar el crediticio", color: "error" });
  };

  const columns = [
    { field: "norecibo", headerName: "No. Recibo", width: 120 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "cedula", headerName: "Cédula", width: 140 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 180 },
    {
      field: "vpagcap",
      headerName: "Pag. Cap.",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "vpagint",
      headerName: "Pag. Int.",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "vpagmora",
      headerName: "Mora",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      // Recibos que solo cobraron el completivo del inicial.
      field: "completivo",
      headerName: "Completivo",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => (Number(value) > 0 ? fmtMoney(value) : ""),
    },
    {
      field: "total",
      headerName: "Total",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            disabled={!params.row.cancelable}
            onClick={() => setTarget(params.row)}
            sx={btnOutline(FP.color.danger || "#D32F2F")}
          >
            Cancelar
          </Button>
          {puedeReimprimir && (
            <Button
              size="small"
              startIcon={<PrintOutlinedIcon />}
              onClick={() => reimprimir(params.row.norecibo)}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Mantenimiento Pagos"
        subtitle="Cancela el último pago de un préstamo y reimprime recibos"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button
              startIcon={<ReceiptLongOutlinedIcon />}
              onClick={crediticio}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Crediticio
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
              <Autocomplete
                freeSolo
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={opts}
                loading={buscando}
                value={prestamoSel}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => setPrestamoSel(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.nombres, o.cedula].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="No. de préstamo o cliente" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={() => cargarRecibos(noprestActual())}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ ...sectionBox, mb: 0 }}>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={48}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmación de cancelación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cancelar pago</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea borrar este registro? Se reversará el recibo #{target?.norecibo} de{" "}
            {target?.nombres} por RD$ {fmtMoney(target?.total)}.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              cancelando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={confirmarCancelacion}
            disabled={cancelando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
